/**
 * @file           request-fields.js
 * @author         Ryan Saweczko, ryansaweczko@distributive.network
 * @date           Oct 2024
 * 
 * Define validator for fields in a request to the operation router
 */
'use strict';

const debugging = require('dcp/debugging').scope('validation');
var debug = require('dcp/build').config.build === 'debug' ? () => true : debugging;

function Field(kind)
{
  this.valueOf = () => kind;
}

/**
 * Define a parameter to be mandatory in a request
 *
 * @param {object|string} kind - type of field
 * @returns {Field} - wrapped kind denoting it's mandatory
 */
exports.mandatory = function mandatory(kind)
{
  var a;
  if (kind instanceof Field)
    a = kind;
  else
    a = new Field(kind);
  a.mandatory = true;
  return a;
}

exports.anyOf = function anyOf(...kinds)
{
  var a;
  a = new Field();
  a.allowedKinds = kinds;
  return a;
}

/**
 * Check that a given object has all properties specified.
 * Additional/missing properties not marked mandatory should result in a warning in debug builds,
 * and incorrectly typed properties return an error.
 * 
 * @param {object} obj - the object to be validated
 * @param {object} fields - object describing the desired shape of obj
 * @param {string} objTrace - for debugging, contains properties accessed to get to current recursion
 * @returns {Error|boolean} - if validation passed, returns true. Else returns an error with what went wrong
 */
exports.validateFields = function validateFields(obj, fields, top = true, objTrace = 'request.')
{
  for (const field in fields)
  {
    let expectedValue = fields[field];
    const validity = validateType(expectedValue, obj, field, objTrace);
    if (validity !== true)
      return validity;
  }

  if (top)
  {
    for (const prop in obj)
    {
      if (!fields.hasOwnProperty(prop))
        debug('fields') && console.warn(`Warning: request has additional property ${objTrace}${prop}`);
    }
  }
  return true;
}

function validateType(expectedType, obj, field, objTrace)
{
  if (expectedType instanceof Field)
  {
    if (expectedType.mandatory && !obj.hasOwnProperty(field))
      return new Error(`Request missing property ${objTrace}${field}`);

    if (expectedType.allowedKinds)
    {
      if (!obj.hasOwnProperty(field))
        return true;
      let invalid = true;
      for (let kind of expectedType.allowedKinds)
      {
        const validity = validateType(kind, obj, field, objTrace);
        if (validity === true)
          invalid = false;
      }
      if (invalid)
      {
        const expectedTypeError = expectedType.allowedKinds.map(t => typeof t === 'string' ? t : JSON.stringify(t));
        return new Error(`Request has improperly typed value: '${objTrace}${field}' was ${typeof obj[field]}, expected ${expectedTypeError.join(' or ')}`);
      }
      return true;
    }
    expectedType = expectedType.valueOf();
  }

  if (!obj.hasOwnProperty(field) || obj[field] === undefined)
    return true;

  if (typeof expectedType === 'string')
  {
    if (typeof obj[field] !== expectedType)
      return new Error(`Request has improperly typed value: '${objTrace}${field}' was ${typeof obj[field]}, expected ${expectedType}`);
  }
  else if (expectedType instanceof Array)
  {
    if (!(obj[field] instanceof Array))
      return new Error(`Request has improperly typed value: '${objTrace}${field}' was ${typeof obj[field]}, expected ${JSON.stringify(expectedType)}`);
    const validity = validateArray(obj[field], expectedType[0], objTrace + `${field}.`);
    if (validity !== true)
      return validity;
  }
  else if (expectedType instanceof Object)
  {
    if (obj[field] === null)
      return true;
    if (!(obj[field] instanceof Object))
      return new Error(`Request has improperly typed value: '${objTrace}${field}' was ${typeof obj[field]}, expected ${JSON.stringify(expectedType)}`);
    const validity = exports.validateFields(obj[field], expectedType, false, objTrace + `${field}.`);
    if (validity !== true)
      return validity;
  }
  else
  {
    // bug in operation fields definition, should be impossible
    throw new Error('Internal Configuration Error');
  }
  return true;
}

function validateArray(array, type, objTrace)
{
  if (typeof type === 'string')
  {
    for (let elem of array)
    {
      if (!(typeof elem === type))
        return new Error(`Request has improperly typed value: '${objTrace}' was ${typeof elem}, expected ${type}`);
    }
  }
  else
  {
    for (let elem of array)
      return exports.validateFields(elem, type, false, objTrace);
  }
  return true;
}